import { useRef, useEffect, useState } from 'react'
import { v4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { useCampaignSetupDispatch } from '../Context/CampaignSetupContext'
import { useDropzone } from 'react-dropzone'
import { useReducerAsync } from 'hooks/useReducerAsync'
import { actions, asyncActionHandlers, fileStatus, initialState, mediaUploadReducer, computeAspectRatio, inferPlacementType, validateMediaFile, validateMediaFileAsync } from './media'
import { groupMediaBySubGroup, getGroupAnchor } from './buildMediaListItems'
import { saveLibraryImages } from 'api/library'
import { googleYouTubeApi } from 'api/connect/google'

const YOUTUBE_WATCH_BASE = 'https://www.youtube.com/watch?v='

// A youtube item loaded from the backend only carries youtube_video_id (the
// wire shape, see mapYoutubeVideoForPayload) — no title yet.
const needsYoutubeHydration = (item) => Boolean(item?.youtube_video_id) && item.title === undefined

// On error (e.g. the video was deleted/made private since being added),
// still return a resolved shape instead of leaving the caller stuck — fall
// back to a minimal title so the card renders with just the ID rather than
// getting stuck hydrating forever.
const hydrateYoutubeVideo = async (item) => {
  const { data, error } = await googleYouTubeApi.validateUrl(
    `${YOUTUBE_WATCH_BASE}${item.youtube_video_id}`,
  )

  return {
    id: item.id,
    source: 'youtube',
    url: `${YOUTUBE_WATCH_BASE}${item.youtube_video_id}`,
    title: error ? item.youtube_video_id : data.title,
    duration_seconds: error ? null : data.duration_seconds,
    thumbnail_url: error ? null : data.thumbnail_url,
    channel_title: error ? null : data.channel_title,
    warnings: error ? [] : data.warnings || [],
  }
}


// service
export const useMediaUploadService = (props) => {
  const {
    initialMedia,
    onChange,
    isCampaignRunning,
    maxItems = Number.MAX_VALUE,
    generatedMediaCount: initialGeneratedMediaCount = 0,
    eventId,
    tc,
    tcRunId,
    isGoogleOnly = false,
    isGoogleSelected = false,
    googleExactRatios = [],
    requiredSlotRatios,
  } = props

  const [state, dispatch] = useReducerAsync(
    mediaUploadReducer,
    {
      ...initialState,
      media: Array.isArray(initialMedia) ? initialMedia : [],
      generatedMediaCount: initialGeneratedMediaCount,
      eventId,
      tc,
      tcRunId,
    },
    asyncActionHandlers,
  )
  const { setHasPendingMedia } = useCampaignSetupDispatch()
  const [expandedImage, setExpandedImage] = useState(null)
  const { t } = useTranslation()

  const { media, generatedMediaCount } = state
  const fileToReplaceRef = useRef(state.fileToReplace)
  fileToReplaceRef.current = state.fileToReplace
  const syncedGeneratedMediaCountRef = useRef(initialGeneratedMediaCount)

  const mediaGroupsCount = groupMediaBySubGroup(media).length
  const occupiedSlots = mediaGroupsCount + (generatedMediaCount ?? 0)
  const canAddInternal = occupiedSlots < maxItems
  const reachedMaxLimit = occupiedSlots >= maxItems
  const remainingSlots = Math.max(0, maxItems - occupiedSlots)

  const isSameMediaItem = (current, previous) =>
    current.id === previous.id &&
    current.status === previous.status &&
    current.sub_group_id === previous.sub_group_id &&
    Boolean(current.generating) === Boolean(previous.generating) &&
    current.title === previous.title &&
    current.thumbnail_url === previous.thumbnail_url

  useEffect(() => {
    const _media = media.filter((x) => ![fileStatus.uploading, fileStatus.new].includes(x.status))
    const _uploadingMedia = media.filter((x) =>
      [fileStatus.uploading, fileStatus.new].includes(x.status),
    )

    const hasMediaDiff =
      _media.some(
        (current) => !initialMedia.some((previous) => isSameMediaItem(current, previous)),
      ) ||
      initialMedia.some(
        (previous) => !_media.some((current) => isSameMediaItem(current, previous)),
      )
    const hasGeneratedCountDiff = generatedMediaCount !== syncedGeneratedMediaCountRef.current
    const hasDiff = hasMediaDiff || hasGeneratedCountDiff

    const hasPendingUploads = _uploadingMedia.length > 0

    if (hasDiff) {
      const removedIdx = initialMedia.findIndex((x) => !_media.some((m) => m.id === x.id))
      syncedGeneratedMediaCountRef.current = generatedMediaCount
      // media and generatedMediaCount are synced together so the backend never
      // gets one field without the other from an in-flight update.
      onChange(_media, removedIdx >= 0 ? removedIdx : null, hasPendingUploads, [], true, generatedMediaCount)
    } else if (hasPendingUploads) {
      setHasPendingMedia(true)
    }
    // Same as v1: do not depend on unstable onChange/initialMedia from parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media, generatedMediaCount, setHasPendingMedia])

  // Hydrates youtube items loaded from the backend with only youtube_video_id
  // (no title/thumbnail yet). Batches every item resolving in the same pass
  // into ONE dispatch rather than one per item — two items resolving in the
  // same tick would otherwise each compute their "next media" from the same
  // stale `media` closure, and whichever dispatch landed second would
  // silently clobber the first (a lost-update race, most visible with
  // duplicate youtube_video_id entries resolving at virtually the same
  // instant).
  const hydratingIdsRef = useRef(new Set())

  useEffect(() => {
    const toHydrate = media.filter(
      (item) => needsYoutubeHydration(item) && !hydratingIdsRef.current.has(item.id),
    )

    if (!toHydrate.length) {
      return undefined
    }

    let cancelled = false
    toHydrate.forEach((item) => hydratingIdsRef.current.add(item.id))

    Promise.all(toHydrate.map(hydrateYoutubeVideo)).then((updates) => {
      updates.forEach((update) => hydratingIdsRef.current.delete(update.id))

      if (cancelled) return

      dispatch({ type: actions.bulkUpdateMediaItems, payload: updates })
    })

    return () => {
      cancelled = true
    }
  }, [media, dispatch])

  const addYoutubeVideo = (video) => {
    if (remainingSlots <= 0) return

    dispatch({
      type: actions.addLibraryMedia,
      payload: [
        {
          ...video,
          id: v4(),
          filename: video.title || 'YouTube video',
          status: fileStatus.success,
        },
      ],
    })
  }

  const onDropFile = async (files) => {
    const validFiles = []
    const errorMessages = []

    // Google only accepts YouTube video references, not uploaded video
    // files — reject them all upfront as one combined message (rather than
    // one per file) instead of also running the size/type/duration checks
    // below, which are meaningless for files that can't be added at all.
    const googleRejectedFiles = isGoogleOnly ? files.filter((file) => file.type.startsWith('video/')) : []
    if (googleRejectedFiles.length > 0) {
      errorMessages.push({
        file: googleRejectedFiles[0],
        errors: [
          t('Recommendations.campaign.setup.media.errors.googleVideoUnsupported', {
            fileNames: googleRejectedFiles.map((file) => file.name).join(', '),
          }),
        ],
      })
    }
    const filesToValidate = googleRejectedFiles.length
      ? files.filter((file) => !googleRejectedFiles.includes(file))
      : files

    for (const file of filesToValidate) {
      const validationError = await validateMediaFileAsync(file, { isGoogleOnly, isGoogleSelected, googleExactRatios, requiredSlotRatios, t })
      if (validationError) {
        errorMessages.push({ file, errors: [validationError] })
      } else {
        validFiles.push(file)
      }
    }
    const hasValidFiles = validFiles.length > 0 ? true : false

    if (errorMessages.length > 0) {
      onChange(media, null, true, errorMessages, hasValidFiles, generatedMediaCount)
    }

    if (validFiles.length > 0) {
      if (fileToReplaceRef.current) {
        dispatch({
          type: actions.replaceFileAsync,
          payload: validFiles[0],
        })
        return
      }

      const filesToAdd = validFiles.slice(0, Math.min(validFiles.length, remainingSlots))
      if (!filesToAdd.length) return

      dispatch({
        type: actions.addFilesAsync,
        payload: filesToAdd,
      })
    }
  }

  const addLibraryItems = (items) => {
    if (remainingSlots <= 0) return

    const usedIds = new Set((media || []).map((file) => String(file.id)))

    const entries = (items || []).map((entry) => entry?.item ?? entry).filter(Boolean)

    // Google only accepts YouTube video references, not uploaded video
    // files — reject library videos the same way validateFile rejects a
    // dropped video file, instead of silently adding them.
    const rejectedItems = []
    const acceptedEntries = entries.filter((item) => {
      const type = item.type || 'image/jpeg'
      if (isGoogleOnly && type.startsWith('video/')) {
        rejectedItems.push(item)
        return false
      }
      return true
    })

    // Same physical validation as any other entry point (size/type/resolution/
    // min-dimensions/ratio) — library items already carry dimensions, so this
    // runs sync, no async dimension extraction needed. Video duration is the
    // one check skipped here: no File object to read it from.
    const rejectedValidationItems = []
    const validatedEntries = acceptedEntries.filter((item) => {
      const meta = item.meta_data ?? {}
      const width = item.width ?? meta.width ?? null
      const height = item.height ?? meta.height ?? null

      const validationError = validateMediaFile(
        {
          type: item.type || 'image/jpeg',
          size: meta.size ?? item.size ?? 0,
          width,
          height,
          fileName: item.filename || item.name || 'library-media',
        },
        { isGoogleOnly, isGoogleSelected, googleExactRatios, requiredSlotRatios, t },
      )

      if (validationError) {
        rejectedValidationItems.push({ item, error: validationError })
        return false
      }

      return true
    })

    const libraryMedia = validatedEntries
      .map((item) => {
        const meta = item.meta_data ?? {}
        const width = item.width ?? meta.width ?? null
        const height = item.height ?? meta.height ?? null

        const libraryId = item.id != null ? String(item.id) : v4()
        const resolvedId = usedIds.has(libraryId) ? v4() : libraryId

        usedIds.add(resolvedId)

        saveLibraryImages({
          id: Number(item.id),
          filename: item.filename || item.name,
          url: item.url,
          size: meta.size ?? item.size ?? 0,
          height,
          width,
          original: item.original,
          ratio: item.ratio || computeAspectRatio(width, height),
          group_id: item.group_id ?? item.id,
          eid: eventId,
          tc,
          tc_run_id: tcRunId,
        })

        return {
          id: resolvedId,
          url: item.url,
          filename: item.filename || item.name || 'library-media',
          name: item.name || item.filename || 'library-media',
          type: item.type || 'image/jpeg',
          size: meta.size ?? item.size ?? 0,
          width,
          height,
          ratio: item.ratio || computeAspectRatio(width, height),
          status: fileStatus.success,
          sub_group_id: v4(),
          headline: null,
          is_generated: false,
        }
      })
      .slice(0, remainingSlots)

    const errorMessages = []

    if (rejectedItems.length > 0) {
      errorMessages.push({
        file: rejectedItems[0],
        errors: [
          t('Recommendations.campaign.setup.media.errors.googleVideoUnsupported', {
            fileNames: rejectedItems.map((item) => item.filename || item.name).join(', '),
          }),
        ],
      })
    }

    rejectedValidationItems.forEach(({ item, error }) => {
      errorMessages.push({ file: item, errors: [error] })
    })

    if (errorMessages.length > 0) {
      onChange(media, null, true, errorMessages, libraryMedia.length > 0, generatedMediaCount)
    }

    if (!libraryMedia.length) return

    dispatch({
      type: actions.addLibraryMedia,
      payload: libraryMedia,
    })
  }

  const addLibraryItemToCoCreateGroup = (libraryItem, groupId, requiredRatio, originalId = null) => {
    if (!libraryItem || !groupId) return

    const usedIds = new Set((media || []).map((file) => String(file.id)))

    const meta = libraryItem.meta_data ?? {}
    const width = libraryItem.width ?? meta.width ?? null
    const height = libraryItem.height ?? meta.height ?? null
    const ratio = libraryItem.ratio || computeAspectRatio(width, height)

    const libraryId = libraryItem.id != null ? String(libraryItem.id) : v4()
    const resolvedId = usedIds.has(libraryId) ? v4() : libraryId

    saveLibraryImages({
      id: Number(libraryItem.id),
      filename: libraryItem.filename || libraryItem.name,
      url: libraryItem.url,
      size: meta.size ?? libraryItem.size ?? 0,
      height,
      width,
      original: libraryItem.original,
      ratio,
      group_id: libraryItem.group_id ?? libraryItem.id,
      eid: eventId,
      tc,
      tc_run_id: tcRunId,
    })

    const groupImages = media.filter((item) => item.sub_group_id === groupId)
    const anchorFile = groupImages[0] ?? null

    let sub_group_id = groupId
    let headline = null

    if (anchorFile) {
      sub_group_id = anchorFile.sub_group_id ?? groupId
      headline = anchorFile.headline ?? null
    } else if (originalId) {
      const original = media.find((item) => item.id === originalId)
      sub_group_id = original?.sub_group_id ?? groupId
    }

    const readyItem = {
      id: resolvedId,
      url: libraryItem.url,
      filename: libraryItem.filename || libraryItem.name || 'library-media',
      name: libraryItem.name || libraryItem.filename || 'library-media',
      type: libraryItem.type || 'image/jpeg',
      size: meta.size ?? libraryItem.size ?? 0,
      width,
      height,
      ratio,
      placement_type: inferPlacementType({ width, height }),
      status: fileStatus.success,
      sub_group_id,
      headline,
      is_generated: false,
    }

    dispatch({
      type: actions.addLibraryMedia,
      payload: [readyItem],
    })
  }

  const replaceLibraryItemInCoCreateGroup = (libraryItem, fileToReplaceId) => {
    if (!libraryItem || !fileToReplaceId) {
      return
    }

    const fileToReplace = media.find((item) => item.id === fileToReplaceId)

    if (!fileToReplace) {
      throw new Error('File to replace not found')
    }

    const usedIds = new Set((media || []).map((file) => String(file.id)))

    const meta = libraryItem.meta_data ?? {}
    const width = libraryItem.width ?? meta.width ?? null
    const height = libraryItem.height ?? meta.height ?? null
    const ratio = libraryItem.ratio || computeAspectRatio(width, height)

    const libraryId = libraryItem.id != null ? String(libraryItem.id) : v4()
    const resolvedId = usedIds.has(libraryId) ? v4() : libraryId

    saveLibraryImages({
      id: Number(libraryItem.id),
      filename: libraryItem.filename || libraryItem.name,
      url: libraryItem.url,
      size: meta.size ?? libraryItem.size ?? 0,
      height,
      width,
      original: libraryItem.original,
      ratio,
      group_id: libraryItem.group_id ?? libraryItem.id,
      eid: eventId,
      tc,
      tc_run_id: tcRunId,
    })

    const readyItem = {
      id: resolvedId,
      url: libraryItem.url,
      filename: libraryItem.filename || libraryItem.name || 'library-media',
      name: libraryItem.name || libraryItem.filename || 'library-media',
      type: libraryItem.type || 'image/jpeg',
      size: meta.size ?? libraryItem.size ?? 0,
      width,
      height,
      ratio,
      placement_type: inferPlacementType({ width, height }),
      status: fileStatus.success,
      sub_group_id: fileToReplace.sub_group_id ?? null,
      headline: null,
      is_generated: false,
    }

    dispatch({
      type: actions.replaceFile,
      payload: { file: readyItem, fileToReplace },
    })
  }

  const removeFile = (fileId) => {
    const file = media.find((x) => x.id === fileId)

    if (file.id === state.fileToReplace?.id) dispatch({ type: actions.resetReplace })
    else dispatch({ type: actions.removeFileAsync, payload: file })
  }

  const removeGroup = (groupId) => {
    dispatch({ type: actions.removeGroupAsync, payload: { groupId } })
  }

  const removeMediaGroup = (groupId) => {
    dispatch({ type: actions.removeMediaGroupAsync, payload: { groupId } })
  }

  const removeFromGroup = (fileId) => {
    dispatch({ type: actions.removeFromGroupAsync, payload: { fileId } })
  }

  const handleAddFileToGroup = async (file, targetFileId, requiredPlacementType) => {
    await dispatch({
      type: actions.addFileToGroupAsync,
      payload: { file, targetFileId, requiredPlacementType },
    })
  }

  const handleReplaceFileInGroup = async (file, fileToReplaceId, requiredPlacementType) => {
    await dispatch({
      type: actions.replaceFileInGroupAsync,
      payload: { file, fileToReplaceId, requiredPlacementType },
    })
  }

  const handleAddFileToCoCreateGroup = async (file, groupId, requiredRatio, originalId = null, requiredSlotRatios) => {
    await dispatch({
      type: actions.addFileToCoCreateGroupAsync,
      payload: { file, groupId, requiredRatio, originalId, requiredSlotRatios, isGoogleOnly, isGoogleSelected, googleExactRatios },
    })
  }

  const handleReplaceFileInCoCreateGroup = async (file, fileToReplaceId, requiredRatio, requiredSlotRatios) => {
    await dispatch({
      type: actions.replaceFileInCoCreateGroupAsync,
      payload: { file, fileToReplaceId, requiredRatio, requiredSlotRatios, isGoogleOnly, isGoogleSelected, googleExactRatios },
    })
  }

  const handleRemoveFileFromCoCreateGroup = async (fileId) => {
    await dispatch({ type: actions.removeFileFromCoCreateGroupAsync, payload: { fileId } })
  }

  const { getInputProps, open } = useDropzone({
    onDrop: onDropFile,
    accept: 'image/jpeg, image/png, video/*',
    multiple: !isCampaignRunning,
    noClick: true,
    noDrag: true,
    noKeyboard: true,
    onFileDialogCancel: () => {
      if (state.fileToReplace) dispatch({ type: actions.resetReplace })
    },
  })

  const getGroupedMediaForNavigation = () =>
    groupMediaBySubGroup(media)
      .map((group) => getGroupAnchor(group))
      .filter(Boolean)
      .map((file) => ({
        groupId: file.sub_group_id ?? file.id,
        feedFile: file.placement_type === 'feed' ? file : null,
        reelFile: file.placement_type === 'reel' ? file : null,
      }))

  const onNext = (currentItem) => {
    const groupedMedia = getGroupedMediaForNavigation()
    
    const currentIndex = groupedMedia.findIndex(
      (item) => {
        if (currentItem.groupId && item.groupId) {
          return currentItem.groupId === item.groupId
        }
        
        const feedMatch = item.feedFile?.id === currentItem.id
        const reelMatch = item.reelFile?.id === currentItem.id
        
        return feedMatch || reelMatch
      }
    )
    
    if (currentIndex >= 0 && currentIndex < groupedMedia.length - 1) {
      const nextItem = groupedMedia[currentIndex + 1]
      const displayFile = nextItem.feedFile || nextItem.reelFile
      setExpandedImage({
        ...displayFile,
        groupId: nextItem.groupId,
        feedFile: nextItem.feedFile,
        reelFile: nextItem.reelFile,
      })
    }
  }

  const onBack = (currentItem) => {
    const groupedMedia = getGroupedMediaForNavigation()
    
    const currentIndex = groupedMedia.findIndex(
      (item) => {
        if (currentItem.groupId && item.groupId) {
          return currentItem.groupId === item.groupId
        }

        const feedMatch = item.feedFile?.id === currentItem.id
        const reelMatch = item.reelFile?.id === currentItem.id
        
        return feedMatch || reelMatch
      }
    )
    
    if (currentIndex > 0) {
      const prevItem = groupedMedia[currentIndex - 1]
      const displayFile = prevItem.feedFile || prevItem.reelFile
      setExpandedImage({
        ...displayFile,
        groupId: prevItem.groupId,
        feedFile: prevItem.feedFile,
        reelFile: prevItem.reelFile,
      })
    }
  }

  // Calculate whether the currently expanded item is the first or last group
  const getExpandedPosition = () => {
    if (!expandedImage) {
      return { isFirstItem: false, isLastItem: false }
    }

    const groupedMedia = getGroupedMediaForNavigation()
    
    const currentIndex = groupedMedia.findIndex((item) => {
      if (expandedImage.groupId && item.groupId) {
        return expandedImage.groupId === item.groupId
      }
      
      const feedMatch = item.feedFile?.id === expandedImage.id
      const reelMatch = item.reelFile?.id === expandedImage.id
      
      return feedMatch || reelMatch
    })

    if (currentIndex === -1) {
      return { isFirstItem: false, isLastItem: false }
    }

    return {
      isFirstItem: currentIndex === 0,
      isLastItem: currentIndex === groupedMedia.length - 1,
    }
  }

  const { isFirstItem, isLastItem } = getExpandedPosition()

  // return
  return {
    media,
    generatedMediaCount,
    canAddInternal,
    reachedMaxLimit,
    mediaGroupsCount,
    expandedImage,
    isFirstItem,
    isLastItem,
    state,
    dispatch,
    onNext,
    onBack,
    removeFile,
    removeGroup,
    removeMediaGroup,
    removeFromGroup,
    handleAddFileToGroup,
    handleReplaceFileInGroup,
    handleAddFileToCoCreateGroup,
    addLibraryItemToCoCreateGroup,
    replaceLibraryItemInCoCreateGroup,
    handleReplaceFileInCoCreateGroup,
    handleRemoveFileFromCoCreateGroup,
    getInputProps,
    open,
    setExpandedImage,
    addLibraryItems,
    addYoutubeVideo,
  }
}